import { RotateCcw, Settings } from "lucide-react";
import { type ReactNode } from "react";
import { useIntl } from "react-intl";

import { Icon } from "../../../atoms/icon";
import {
  DropdownMenuItem,
  DropdownMenuSeparator,
} from "../../../molecules/dropdown-menu";
import { useDataTableContext } from "../../context";
import { useDataTableSettingsContext } from "../../context/data-table-settings-context";
import { DataTableSettingsProvider } from "../../context/data-table-settings-provider";
import { DataTablePreferences } from "../data-table-preferences/data-table-preferences";
import { DataTableCaptionButton } from "./data-table-caption-button";
import {
  DataTableRefetchButton,
  useDataTableRefetch,
} from "./data-table-refetch-button";

function DataTableRefetch() {
  const intl = useIntl();

  const refetch = useDataTableRefetch();

  return (
    <DropdownMenuItem onSelect={refetch}>
      <Icon Icon={RotateCcw} className="mr-2 size-4" />

      {intl.formatMessage({
        id: "data-table.refetch",
        defaultMessage: "Načíst znovu",
      })}
    </DropdownMenuItem>
  );
}

function DataTableSettingsButton({ children }: { children?: ReactNode }) {
  const intl = useIntl();
  const table = useDataTableContext();
  const { triggerRef } = useDataTableSettingsContext();

  return (
    <DataTablePreferences
      trigger={
        <DataTableCaptionButton
          ref={triggerRef}
          iconLeft={{
            Icon: Settings,
            className: "size-4",
          }}
          tooltip={intl.formatMessage({
            id: "data-table.settings",
            defaultMessage: "Nastavení",
          })}
          isLoading={
            table.tableCaption.showRefetch && table.dataQuery.isRefetching
          }
        />
      }
    >
      {children}
    </DataTablePreferences>
  );
}

export function DataTableSettings({ children }: { children?: ReactNode }) {
  const {
    tableCaption: { showRefetch, showPreferences },
  } = useDataTableContext();

  if (!showRefetch && !showPreferences) {
    return null;
  }

  if (showPreferences) {
    return (
      <DataTableSettingsProvider>
        <DataTableSettingsButton>
          {showRefetch && (
            <>
              <DataTableRefetch />
              <DropdownMenuSeparator />
            </>
          )}
          {children}
        </DataTableSettingsButton>
      </DataTableSettingsProvider>
    );
  }

  return <DataTableRefetchButton />;
}
